/**
 * Email service configuration page
 *
 * @description Admin SMTP configuration
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Button,
  Card,
  Switch,
  InputNumber,
  Divider,
  Space,
  Alert,
  Spin,
  App,
  Modal,
} from 'antd';
import {
  MailOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  SaveOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import StandardPageLayout from '../../components/layout/StandardPageLayout';
import { apiSlice } from '../../store/api/apiSlice';

interface SmtpProvider {
  key: 'qq' | 'mail163' | 'gmail' | 'aliyun' | 'qqEnterprise' | 'outlook';
  host: string;
  port: string;
  note?: 'authCodeRequired' | 'appPasswordRequired';
}

const SMTP_PROVIDERS: SmtpProvider[] = [
  {
    key: 'qq',
    host: 'smtp.qq.com',
    port: '587 (STARTTLS) / 465 (SSL)',
    note: 'authCodeRequired',
  },
  {
    key: 'mail163',
    host: 'smtp.163.com',
    port: '25 / 465 / 994',
    note: 'authCodeRequired',
  },
  {
    key: 'gmail',
    host: 'smtp.gmail.com',
    port: '587 (STARTTLS) / 465 (SSL)',
    note: 'appPasswordRequired',
  },
  {
    key: 'aliyun',
    host: 'smtp.qiye.aliyun.com',
    port: '465 (SSL)',
  },
  {
    key: 'qqEnterprise',
    host: 'smtp.exmail.qq.com',
    port: '465 (SSL)',
  },
  {
    key: 'outlook',
    host: 'smtp-mail.outlook.com',
    port: '587 (STARTTLS)',
  },
];

// Email configuration API
const emailConfigApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getEmailConfig: builder.query<
      {
        success: boolean;
        data: {
          configured: boolean;
          config: {
            smtp: {
              host: string;
              port: number;
              secure: boolean;
              auth: {
                user: string;
                pass: string;
              };
            };
            from: {
              name: string;
              email: string;
            };
            enabled: boolean;
            updatedAt?: string;
          };
        };
      },
      void
    >({
      query: () => '/admin/email-config',
    }),

    updateEmailConfig: builder.mutation<
      { success: boolean; message: string },
      {
        smtp: {
          host: string;
          port: number;
          secure: boolean;
          auth: {
            user: string;
            pass: string;
          };
        };
        from: {
          name: string;
          email: string;
        };
        enabled: boolean;
      }
    >({
      query: body => ({
        url: '/admin/email-config',
        method: 'PUT',
        body,
      }),
    }),

    testEmailConfig: builder.mutation<
      {
        success: boolean;
        message: string;
        data?: { messageId: string; sentTo: string };
      },
      {
        testEmail: string;
        smtp?: {
          host: string;
          port: number;
          secure: boolean;
          auth: {
            user: string;
            pass: string;
          };
        };
      }
    >({
      query: body => ({
        url: '/admin/email-config/test',
        method: 'POST',
        body,
      }),
    }),
  }),
});

const {
  useGetEmailConfigQuery,
  useUpdateEmailConfigMutation,
  useTestEmailConfigMutation,
} = emailConfigApi;

interface EmailFormData {
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPass: string;
  fromName: string;
  fromEmail: string;
  enabled: boolean;
}

const EmailConfigPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<EmailFormData>();
  const [testEmailForm] = Form.useForm<{ testEmail: string }>();
  const [testModalVisible, setTestModalVisible] = useState(false);

  const { data, isLoading, refetch } = useGetEmailConfigQuery();
  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateEmailConfigMutation();
  const [testConfig, { isLoading: isTesting }] = useTestEmailConfigMutation();

  // Load config into form
  useEffect(() => {
    if (data?.data?.config) {
      const config = data.data.config;
      form.setFieldsValue({
        smtpHost: config.smtp.host,
        smtpPort: config.smtp.port,
        smtpSecure: config.smtp.secure,
        smtpUser: config.smtp.auth.user,
        smtpPass: config.smtp.auth.pass,
        fromName: config.from.name,
        fromEmail: config.from.email,
        enabled: config.enabled,
      });
    }
  }, [data, form]);

  const handleSave = async (values: EmailFormData) => {
    try {
      const result = await updateConfig({
        smtp: {
          host: values.smtpHost,
          port: values.smtpPort,
          secure: values.smtpSecure,
          auth: {
            user: values.smtpUser,
            pass: values.smtpPass,
          },
        },
        from: {
          name: values.fromName,
          email: values.fromEmail,
        },
        enabled: values.enabled,
      }).unwrap();

      if (result.success) {
        message.success(t('systemSettings:emailConfig.messages.saveSuccess'));
        refetch();
      } else {
        message.error(
          result.message || t('systemSettings:emailConfig.messages.saveFailed')
        );
      }
    } catch (error: any) {
      message.error(
        error?.data?.error?.message ||
          t('systemSettings:emailConfig.messages.saveConfigFailed')
      );
    }
  };

  const handleTest = async (values: { testEmail: string }) => {
    try {
      // Support testing with unsaved SMTP values from current form.
      const formValues = form.getFieldsValue();
      const result = await testConfig({
        testEmail: values.testEmail,
        smtp: {
          host: formValues.smtpHost,
          port: formValues.smtpPort,
          secure: formValues.smtpSecure,
          auth: {
            user: formValues.smtpUser,
            pass: formValues.smtpPass,
          },
        },
      }).unwrap();

      if (result.success) {
        message.success(t('systemSettings:emailConfig.messages.testSuccess'));
        setTestModalVisible(false);
        testEmailForm.resetFields();
      } else {
        message.error(
          result.message || t('systemSettings:emailConfig.messages.sendFailed')
        );
      }
    } catch (error: any) {
      message.error(
        error?.data?.error?.message ||
          t('systemSettings:emailConfig.messages.sendTestFailed')
      );
    }
  };

  const isConfigured = data?.data?.configured;
  const updatedAtText = data?.data?.config?.updatedAt
    ? new Date(data.data.config.updatedAt).toLocaleString(
        i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
      )
    : t('systemSettings:emailConfig.status.unknown');

  return (
    <StandardPageLayout
      title={t('systemSettings:emailConfig.title')}
      subtitle={t('systemSettings:emailConfig.subtitle')}
      breadcrumbs={[
        { label: t('systemSettings:breadcrumb.home'), path: '/admin' },
        { label: t('systemSettings:breadcrumb.systemSettings') },
        { label: t('systemSettings:breadcrumb.emailConfig') },
      ]}
    >
      <Spin spinning={isLoading}>
        <div className="w-full">
          {/* Status alerts */}
          {!isConfigured && (
            <Alert
              type="warning"
              message={t(
                'systemSettings:emailConfig.status.notConfiguredTitle'
              )}
              description={t(
                'systemSettings:emailConfig.status.notConfiguredDescription'
              )}
              showIcon
              className="mb-6"
            />
          )}

          {isConfigured && data?.data?.config?.enabled && (
            <Alert
              type="success"
              message={t('systemSettings:emailConfig.status.enabledTitle')}
              description={t('systemSettings:emailConfig.status.lastUpdated', {
                time: updatedAtText,
              })}
              showIcon
              className="mb-6"
              icon={<CheckCircleOutlined />}
            />
          )}

          <Card
            title={
              <span className="flex items-center gap-2">
                <MailOutlined className="text-emerald-600" />
                {t('systemSettings:emailConfig.card.smtpConfig')}
              </span>
            }
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isLoading}
                >
                  {t('common:action.refresh')}
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => setTestModalVisible(true)}
                  disabled={!isConfigured || !data?.data?.config?.enabled}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {t('systemSettings:emailConfig.actions.sendTestEmail')}
                </Button>
              </Space>
            }
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                smtpPort: 587,
                smtpSecure: false,
                enabled: true,
                fromName: t('common:app.title'),
              }}
            >
              {/* SMTP server settings */}
              <div className="mb-6">
                <h3 className="text-base font-medium text-gray-700 mb-4">
                  {t('systemSettings:emailConfig.sections.smtpServer')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="smtpHost"
                    label={t('systemSettings:emailConfig.fields.smtpHost')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'systemSettings:emailConfig.validation.smtpHostRequired'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'systemSettings:emailConfig.fields.smtpHostPlaceholder'
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    name="smtpPort"
                    label={t('systemSettings:emailConfig.fields.smtpPort')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'systemSettings:emailConfig.validation.smtpPortRequired'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={65535}
                      placeholder={t(
                        'systemSettings:emailConfig.fields.smtpPortPlaceholder'
                      )}
                      className="w-full"
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="smtpSecure"
                  label={t('systemSettings:emailConfig.fields.smtpSecure')}
                  valuePropName="checked"
                  extra={t('systemSettings:emailConfig.fields.smtpSecureHelp')}
                >
                  <Switch
                    checkedChildren={t('systemSettings:emailConfig.fields.ssl')}
                    unCheckedChildren={t(
                      'systemSettings:emailConfig.fields.starttls'
                    )}
                  />
                </Form.Item>
              </div>

              <Divider />

              {/* Authentication settings */}
              <div className="mb-6">
                <h3 className="text-base font-medium text-gray-700 mb-4">
                  <LockOutlined className="mr-2" />
                  {t('systemSettings:emailConfig.sections.authInfo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="smtpUser"
                    label={t('systemSettings:emailConfig.fields.smtpUser')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'systemSettings:emailConfig.validation.smtpUserRequired'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'systemSettings:emailConfig.fields.smtpUserPlaceholder'
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    name="smtpPass"
                    label={t('systemSettings:emailConfig.fields.smtpPass')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'systemSettings:emailConfig.validation.smtpPassRequired'
                        ),
                      },
                    ]}
                    extra={t('systemSettings:emailConfig.fields.smtpPassHelp')}
                  >
                    <Input.Password
                      placeholder={t(
                        'systemSettings:emailConfig.fields.smtpPassPlaceholder'
                      )}
                    />
                  </Form.Item>
                </div>
              </div>

              <Divider />

              {/* Sender information */}
              <div className="mb-6">
                <h3 className="text-base font-medium text-gray-700 mb-4">
                  {t('systemSettings:emailConfig.sections.senderInfo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="fromName"
                    label={t('systemSettings:emailConfig.fields.fromName')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'systemSettings:emailConfig.validation.fromNameRequired'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'systemSettings:emailConfig.fields.fromNamePlaceholder'
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    name="fromEmail"
                    label={t('systemSettings:emailConfig.fields.fromEmail')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'systemSettings:emailConfig.validation.fromEmailRequired'
                        ),
                      },
                      {
                        type: 'email',
                        message: t(
                          'systemSettings:emailConfig.validation.emailInvalid'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'systemSettings:emailConfig.fields.fromEmailPlaceholder'
                      )}
                    />
                  </Form.Item>
                </div>
              </div>

              <Divider />

              {/* Enable switch */}
              <Form.Item
                name="enabled"
                label={t('systemSettings:emailConfig.fields.enabled')}
                valuePropName="checked"
              >
                <Switch
                  checkedChildren={<CheckCircleOutlined />}
                  unCheckedChildren={<CloseCircleOutlined />}
                />
              </Form.Item>

              {/* Save button */}
              <Form.Item className="mb-0 pt-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isUpdating}
                  size="large"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {t('systemSettings:emailConfig.actions.saveConfig')}
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Common SMTP examples */}
          <Card
            title={t('systemSettings:emailConfig.card.commonSmtp')}
            className="mt-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {SMTP_PROVIDERS.map(provider => (
                <div key={provider.key} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800 mb-2">
                    {t(
                      `systemSettings:emailConfig.providers.${provider.key}.name`
                    )}
                  </div>
                  <div className="text-gray-600">
                    <div>
                      {t('systemSettings:emailConfig.providers.host', {
                        host: provider.host,
                      })}
                    </div>
                    <div>
                      {t('systemSettings:emailConfig.providers.port', {
                        port: provider.port,
                      })}
                    </div>
                    {provider.note && (
                      <div className="text-orange-600">
                        {t(
                          `systemSettings:emailConfig.providers.notes.${provider.note}`
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </Spin>

      {/* Test email modal */}
      <Modal
        title={t('systemSettings:emailConfig.modal.testEmailTitle')}
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        footer={null}
        destroyOnClose
        width={400}
      >
        <Form
          form={testEmailForm}
          layout="vertical"
          onFinish={handleTest}
          className="pt-4"
        >
          <Form.Item
            name="testEmail"
            label={t('systemSettings:emailConfig.fields.testEmail')}
            rules={[
              {
                required: true,
                message: t(
                  'systemSettings:emailConfig.validation.testEmailRequired'
                ),
              },
              {
                type: 'email',
                message: t(
                  'systemSettings:emailConfig.validation.emailInvalid'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'systemSettings:emailConfig.fields.testEmailPlaceholder'
              )}
              size="large"
            />
          </Form.Item>
          <Form.Item className="mb-0 pt-2">
            <Space className="w-full justify-end">
              <Button onClick={() => setTestModalVisible(false)}>
                {t('common:action.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isTesting}
                icon={<SendOutlined />}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {t('systemSettings:emailConfig.actions.sendTest')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </StandardPageLayout>
  );
};

export default EmailConfigPage;
